import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  TDCTransaction,
  TransactionModel,
  TransactionWrapper,
} from '@core/models';
import { ConnectionService } from '@features/pages/checkout/services/connection.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  connectionService = inject(ConnectionService);
  router = inject(Router);
  transaction$: BehaviorSubject<TransactionWrapper | null> =
    new BehaviorSubject<TransactionWrapper | null>(null);

  hasRainoutInsurance(): boolean {
    return Boolean(
      this.transaction$.value?.transaction?.tdcTransaction?.hasInsuranceRainout
    );
  }

  getEventId(): number {
    return this.transaction$.value?.transaction?.eventId || 0;
  }

  getTotalSeats(): number {
    return this.transaction$.value?.transaction?.seats?.length || 0;
  }

  getTotalPrice(): number {
    return (
      this.transaction$.value?.transaction?.tdcTransaction?.totalPriceSeats || 0
    );
  }

  getUnitPrice(): number {
    if (!this.transaction$.value?.transaction?.seatsPrices) {
      return 0;
    }
    return (
      Object.values(this.transaction$.value?.transaction?.seatsPrices)?.[0]
        ?.value || 0
    );
  }

  getTransactionUpsell() {
    const buyerTypeId = this.transaction$.value?.transaction?.buyerTypeId;
    return this.transaction$.value?.transaction?.event?.upsells?.find(
      upsell => upsell.buyerTypeId === buyerTypeId
    );
  }

  getInsurancePrice(): number {
    return (
      this.transaction$.value?.transaction?.tdcTransaction
        ?.insuranceRainoutPrice || 0
    );
  }

  getServiceChargesDetails(t: TDCTransaction): {
    name: string;
    amount: number;
  }[] {
    const calculatedServiceCharges = Object.values(
      t.serviceChargesDetails.clientServiceCharges ?? {}
    );

    const calculatedServiceChargesAmount = calculatedServiceCharges.reduce(
      (acc, item) => acc + (item?.total ?? 0),
      0
    );

    let actualServiceCharges = t.balance - (t.totalPriceSeats ?? 0);

    if (t.hasInsuranceRainout) {
      const actualServiceChargesWithoutInsurance =
        actualServiceCharges - (t.insuranceRainoutPrice ?? 0);
      actualServiceCharges =
        actualServiceChargesWithoutInsurance > 0
          ? actualServiceChargesWithoutInsurance
          : actualServiceCharges;
    }

    // const transactionUpsell = this.getTransactionUpsell();
    // if (transactionUpsell) {
    //   const transactionUpsellPrice =
    //     (transactionUpsell.price ?? 0) * this.getTotalSeats();
    //   actualServiceCharges = actualServiceCharges - transactionUpsellPrice;
    // }

    const hasNoConfiguredServiceCharges = calculatedServiceChargesAmount === 0;

    if (
      hasNoConfiguredServiceCharges ||
      calculatedServiceChargesAmount > actualServiceCharges
    ) {
      const cartServiceCharges = Object.values(
        t.serviceChargesDetails.cartServiceCharges
      ).filter(sc => sc.inclusiveCharge == false);

      const sc = cartServiceCharges.reduce<{ name: string; amount: number }[]>(
        (acc, item) => {
          const existing = acc.find(v => v.name === item.publicDescription);

          if (existing) {
            existing.amount += item.total;
          } else {
            acc.push({
              name: item.publicDescription,
              amount: item.total,
            });
          }

          return acc;
        },
        []
      );

      return sc;
    }

    const result = calculatedServiceCharges.map(item => {
      return { name: item.name, amount: item.total };
    });

    if (calculatedServiceChargesAmount < actualServiceCharges) {
      result.push({
        name: 'Others',
        amount: actualServiceCharges - calculatedServiceChargesAmount,
      });
    }

    return result;
  }

  restartTransaction() {
    const transaction = this.transaction$.value;
    if (!transaction) {
      return;
    }
    const transactionId = transaction.transaction.id;
    this.connectionService
      .deleteTransaction(transactionId.toString())
      .subscribe({
        next: response => {
          this.goSeatSelection();
        },
        error: error => {
          // no need for a modal here
          this.goSeatSelection();
        },
      });
  }

  private goSeatSelection() {
    const transaction = this.transaction$.value;

    let eventId: number = 0;
    if (transaction) {
      eventId = (transaction.transaction as Required<TransactionModel>).event
        .pvEventId;
    }
    this.router.navigate(['seat-selection'], {
      queryParams: { event: eventId, 'restore-cart': '1' },
    });
  }
}
